import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { SpousalSupportPayment } from "../models/SpousalSupportPayment";
import { SpousalSupportPaymentService } from "../services/SpousalSupportPaymentService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface SpousalSupportPaymentContextProps {
  spousalSupportPayments: SpousalSupportPayment[];
  selectedSpousalSupportPayment: SpousalSupportPayment | null;
  setSelectedSpousalSupportPayment: (id: string) => void;
  createSpousalSupportPayment: (
    data: Omit<
      SpousalSupportPayment,
      "_id" | "createdAt" | "updatedAt" | "matter"
    >,
  ) => Promise<string>;
  updateSpousalSupportPayment: (
    id: string,
    data: Partial<Omit<SpousalSupportPayment, "_id" | "createdAt">>,
  ) => Promise<void>;
  deleteSpousalSupportPaymentById: (id: string) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const SpousalSupportPaymentContext = createContext<
  SpousalSupportPaymentContextProps | undefined
>(undefined);

export const useSpousalSupportPayment =
  (): SpousalSupportPaymentContextProps => {
    const context = useContext(SpousalSupportPaymentContext);
    if (!context)
      throw new Error(
        "useSpousalSupportPayment must be used within SpousalSupportPaymentProvider",
      );
    return context;
  };

export const SpousalSupportPaymentProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const { currentMatterId } = useMatter();
  const [spousalSupportPayments, setSpousalSupportPayments] = useState<
    SpousalSupportPayment[]
  >([]);
  const [selectedSpousalSupportPayment, setSelected] =
    useState<SpousalSupportPayment | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({ list: true });

  const fetchPayments = async () => {
    if (!currentMatterId) return;
    try {
      const payments =
        await MatterService.getSpousalSupportPayments(currentMatterId);
      setSpousalSupportPayments(payments);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  useEffect(() => {
    if (user && currentMatterId) {
      setLoadings((prev) => ({ ...prev, list: true }));
      fetchPayments();
    }
  }, [user, currentMatterId]);

  const setSelectedSpousalSupportPayment = async (id: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const payment = await SpousalSupportPaymentService.get(id);
      setSelected(payment);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createSpousalSupportPayment = async (
    data: Omit<
      SpousalSupportPayment,
      "_id" | "createdAt" | "updatedAt" | "matter"
    >,
  ): Promise<string> => {
    const newId = await SpousalSupportPaymentService.create({
      ...data,
      matter: currentMatterId!,
    });
    await fetchPayments();
    return newId;
  };

  const updateSpousalSupportPayment = async (
    id: string,
    data: Partial<Omit<SpousalSupportPayment, "_id" | "createdAt">>,
  ): Promise<void> => {
    await SpousalSupportPaymentService.update(id, data);
    await setSelectedSpousalSupportPayment(id);
  };

  const deleteSpousalSupportPaymentById = async (id: string) => {
    await SpousalSupportPaymentService.delete(id);
    setSpousalSupportPayments((prev) => prev.filter((p) => p._id !== id));
    setSelected(null);
  };

  return (
    <SpousalSupportPaymentContext.Provider
      value={{
        spousalSupportPayments,
        selectedSpousalSupportPayment,
        setSelectedSpousalSupportPayment,
        createSpousalSupportPayment,
        updateSpousalSupportPayment,
        deleteSpousalSupportPaymentById,
        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </SpousalSupportPaymentContext.Provider>
  );
};
